import React, { useRef, useState } from 'react';
import versionData from '../../version.json';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import { setShowAdminDetails } from '../../store/slices/uiSlice';
import { Pencil, Loader2 } from 'lucide-react';
import { uploadProfileImage } from '../../config/firebaseAppConfig';
import { farmvestService } from '../../services/farmvest_api';
import { fetchAdminProfile } from '../../store/slices/usersSlice';

interface AdminDetailsModalProps {
    adminName?: string;
    adminMobile?: string;
    adminRole?: string;
    lastLogin?: string;
    presentLogin?: string;
    profileImageSrc?: string | null;
    onLogout?: () => void;
}

const AdminDetailsModal: React.FC<AdminDetailsModalProps> = (props) => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state: RootState) => state.ui.showAdminDetails);
    const auth = useAppSelector((state: RootState) => state.auth);
    const { adminProfile } = useAppSelector((state: RootState) => state.users);

    const adminName = props.adminName || auth.adminName;
    const adminMobile = props.adminMobile || auth.adminMobile;
    const adminRole = props.adminRole || auth.adminRole;
    const lastLogin = props.lastLogin || auth.lastLogin;
    const presentLogin = props.presentLogin || auth.presentLogin;
    const profileImageSrc = props.profileImageSrc;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);

    const onClose = () => {
        dispatch(setShowAdminDetails(false));
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !adminMobile) return;

        setIsUploading(true);
        try {
            const empId = adminProfile?.user_id;
            const uploadedUrl = await uploadProfileImage(file, adminMobile);
            
            if (empId) {
                await farmvestService.updateEmployee({
                    user_id: Number(empId),
                    profile_image_url: uploadedUrl
                });
            } else {
                // If it's a regular user or missing emp ID, attempt user details update
                await farmvestService.updateUserDetails({
                    profile: uploadedUrl
                });
            }

            // Refresh the profile to update everywhere
            dispatch(fetchAdminProfile(adminMobile));
        } catch (error) {
            alert('Failed to upload profile picture. Please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div onClick={onClose} className="admin-modal-overlay fixed inset-0 z-[1999] bg-transparent">
            <div
                className="admin-popover admin-modal-container absolute top-[75px] right-[12px] md:right-[25px] left-auto w-[calc(100vw-24px)] md:w-80 max-w-[320px] bg-white rounded-xl shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] border border-[#e5e7eb] z-[2000] p-5 animate-admin-fade-in"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="admin-modal-close-btn absolute top-2 right-2 bg-transparent border-none text-xl text-[#9ca3af] cursor-pointer p-1 leading-none"
                >
                    ×
                </button>
                <div className="admin-modal-header flex items-center gap-4 mb-4">
                    <div className="relative flex-shrink-0">
                        <div className="admin-avatar w-12 h-12 rounded-full bg-[#e0e7ff] text-[#4f46e5] text-xl font-bold flex items-center justify-center relative overflow-hidden">
                            <span className="absolute inset-0 flex items-center justify-center z-0">
                                {adminName.charAt(0).toUpperCase()}
                            </span>
                            {profileImageSrc && (
                                <img 
                                    src={profileImageSrc} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover relative z-10" 
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            )}
                        </div>
                        
                        {/* Edit Icon Button */}
                        <button
                            onClick={() => !isUploading && fileInputRef.current?.click()}
                            disabled={isUploading}
                            className="absolute -bottom-1 -right-1 w-[20px] h-[20px] bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm text-gray-500 hover:text-[#4f46e5] hover:border-[#4f46e5] transition-colors z-20 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Edit Profile Picture"
                        >
                            {isUploading ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                                <Pencil className="w-3 h-3" />
                            )}
                        </button>

                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleFileChange} 
                            accept="image/*" 
                            className="hidden" 
                        />
                    </div>
                    <div className="admin-info overflow-hidden">
                        <h2 className="admin-name m-0 text-base text-[#1f2937] whitespace-nowrap overflow-hidden text-ellipsis">{adminName}</h2>
                        <p className="admin-mobile m-0 text-[0.8rem] text-[#6b7280]">{adminMobile}</p>
                    </div>
                </div>

                <div className="admin-details-card grid gap-3 bg-[#f9fafb] p-4 rounded-lg text-sm">
                    <div>
                        <div className="admin-detail-label text-xs text-[#6b7280] mb-0.5">Role</div>
                        <div className="admin-detail-value font-semibold text-[#374151]">{adminRole}</div>
                    </div>

                    <div>
                        <div className="admin-detail-label text-xs text-[#6b7280] mb-0.5">Last Login</div>
                        <div className="admin-detail-value font-semibold text-[#374151]">{lastLogin || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="admin-detail-label text-xs text-[#6b7280] mb-0.5">Present Login</div>
                        <div className="admin-detail-value font-semibold text-[#374151]">{presentLogin || 'N/A'}</div>
                    </div>
                </div>

                <div className="mt-4 px-4">
                    <button
                        onClick={props.onLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-log-out"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></svg>
                        Logout
                    </button>
                </div>

                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>v{versionData.version}</span>
                </div>
            </div>
        </div>
    );
};

export default AdminDetailsModal;
